import clsx from 'clsx'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import { Button } from '@wartek-id/button'
import { Text } from '@wartek-id/text'

import { usePostQuizProgress } from 'api/microlearning/useAPIMateri'
import ConfirmNavigateAwayDialog from '../../components/ConfirmNavigateAwayDialog'
import { EmptyState } from 'components/EmptyState'
import { GATrackEvent } from 'utils/googleAnalytics'
import usePreventRouteChangeIf from 'utils/hooks/usePreventRouteChangeIf'
import QuizDoneDialog from './QuizDoneDialog'
import {
  SingleQuestionOptions,
  MultipleQuestionOptions,
} from './QuestionOptions'
import QuestionIndicator from './QuestionIndicator'

import type { QuizAnswer } from 'types/microlearning'
import type { ResponseQuiz } from 'api/microlearning/types'
import type { Question } from 'types/microlearning'

interface QuizAppProps {
  data: ResponseQuiz
  materialId: string | number
  isDesktop?: boolean
}

const isAnswerCorrect: (
  Question: Question,
  Answer: string | string[]
) => boolean = (Question, Answer) => {
  const selectedOption = Question.answerOptions.find(
    (option) => option.key === Answer
  )
  return selectedOption?.isCorrect ?? false
}

const QuizApp = ({ data, materialId, isDesktop = false }: QuizAppProps) => {
  const { query, back } = useRouter()
  const [showAnswers, setShowAnswers] = useState(false)
  const [showNavigateAwayDialog, setShowNavigateAwayDialog] = useState(false)
  const [isDone, setIsDone] = useState(false)
  const [answers, setAnswers] = useState<QuizAnswer[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [currentAnswer, setCurrentAnswer] = useState<QuizAnswer>({
    questionId: null,
    answer: null,
    isCorrect: false,
  })
  const contentRef = React.useRef(null)

  useEffect(() => {
    GATrackEvent({
      eventName: 'learning_activity',
      eventParams: {
        event_trigger: 'module_open_quiz',
        action_type: 'open_material_quiz',
        meta: JSON.stringify({
          material_id: materialId,
          module_id: query.modul,
        }),
      },
      sendDefaultParams: true,
    })
  }, [materialId, query.modul])

  const { reset: resetPreventRouteChange } = usePreventRouteChangeIf(
    true,
    () => {
      setShowNavigateAwayDialog(true)
    },
    true
  )

  const { mutateAsync, isLoading: isSubmittingForm } = usePostQuizProgress(
    query.modul as string,
    query.id as string,
    {
      onSuccess: () => {
        GATrackEvent({
          eventName: 'learning_activity',
          eventParams: {
            event_trigger: 'quiz_complete_quiz',
            action_type: 'complete_material_quiz',
            meta: JSON.stringify({
              material_id: materialId,
              module_id: query.modul,
            }),
          },
          sendDefaultParams: true,
        })
        setIsDone(true)
      },
    }
  )

  const gotoNextQuestion = async (questionsCount: number) => {
    if (questionsCount <= currentIndex + 1) {
      resetPreventRouteChange()
      await mutateAsync({
        quiz: answers.map((answer) => {
          return typeof answer.answer === 'string'
            ? {
                ...answer,
                answer: [answer.answer],
              }
            : answer
        }),
      })
    } else {
      setCurrentAnswer({
        questionId: null,
        answer: null,
        isCorrect: false,
      })
      setShowAnswers(false)
      setCurrentIndex((prev) => prev + 1)
      contentRef?.current?.scrollTo(0, 0)
    }
  }

  return (
    <>
      <Head>
        <title>
          Pelatihan Mandiri | Latihan Pemahaman materi {data?.name ?? ''}
        </title>
      </Head>
      <ConfirmNavigateAwayDialog
        isOpen={showNavigateAwayDialog}
        onClose={() => setShowNavigateAwayDialog(false)}
        onConfirmed={() => {
          resetPreventRouteChange()
          back()
        }}
        title="Keluar Dari Latihan Pemahaman?"
        description="Jika keluar sekarang, progres Latihan Pemahaman tidak akan tersimpan. Tetap keluar?"
        confirmText="Ya, keluar"
        cancelText="Lanjut Kerjakan Latihan"
      />

      {!data && (
        <div>
          <EmptyState
            title="Kuis tidak tersedia"
            description="Materi ini tidak memiliki kuis."
          />
        </div>
      )}

      {!!data && (
        <>
          <QuizDoneDialog
            refleksiIsDone={false}
            canDoRefleksi={false}
            isOpen={isDone}
            onClose={() => {
              setIsDone(false)
            }}
            onConfirmed={() => {
              resetPreventRouteChange()
              setTimeout(() => {
                back()
              }, 50)
            }}
          />

          <div
            ref={contentRef}
            className="py-4 overflow-y-auto "
            style={{
              height: `calc(100vh - ${isDesktop ? 15 : 8.25}rem)`,
            }}
          >
            <div className="px-4">
              <QuestionIndicator
                currentIndex={currentIndex}
                totalQuestions={data?.questions?.length}
              />

              <div className="mb-2">
                <Text
                  as="div"
                  color="default"
                  variant="heading-md"
                  dangerouslySetInnerHTML={{
                    __html: data?.questions[currentIndex]?.text,
                  }}
                />
              </div>

              <div
                className={clsx(
                  'relative',
                  showAnswers &&
                    'before:absolute before:w-full before:h-full before:top-0 before:left-0 before:z-10'
                )}
              >
                <div className="z-0">
                  {data?.questions[currentIndex]?.type === 'single' && (
                    <SingleQuestionOptions
                      page={currentIndex}
                      onSelected={(hasAnswer, answer) => {
                        setCurrentAnswer({
                          questionId: data?.questions[currentIndex]?.id,
                          answer: answer,
                          isCorrect: isAnswerCorrect(
                            data?.questions[currentIndex],
                            answer
                          ),
                        })
                      }}
                      showAnswers={showAnswers}
                      options={data?.questions[currentIndex]?.answerOptions}
                    />
                  )}

                  {data?.questions[currentIndex]?.type === 'multiple' && (
                    <MultipleQuestionOptions
                      onSelected={(hasAnswer, answer) => {
                        setCurrentAnswer({
                          questionId: data?.questions[currentIndex]?.id,
                          answer: answer,
                          isCorrect: false,
                        })
                      }}
                      showAnswers={showAnswers}
                      options={data?.questions[currentIndex]?.answerOptions}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            className={`${
              isDesktop ? 'mt-0 bg-white' : 'fixed'
            } bottom-0 left-0 w-full`}
          >
            <div className="p-4 max-w-xl mx-auto">
              {!showAnswers && (
                <Button
                  onClick={() => {
                    setAnswers((old) => {
                      return [...old, currentAnswer]
                    })
                    setShowAnswers(true)
                  }}
                  color="black"
                  disabled={
                    currentAnswer.questionId === null ||
                    currentAnswer.answer.length === 0
                  }
                  fullWidth
                >
                  Jawab
                </Button>
              )}

              {showAnswers && (
                <Button
                  onClick={() => gotoNextQuestion(data?.questions?.length)}
                  color="black"
                  fullWidth
                  isLoading={isSubmittingForm}
                >
                  Lanjut
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default QuizApp
